import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useGetPolicyQuery,
  useGetPolicyCoverageQuery,
  useRecordPolicyReviewMutation,
  useSetPolicyPaperBasedMutation,
  useCreatePolicyVersionMutation,
  useLazyGetPolicyVersionFileQuery,
  useUpdatePolicyMutation,
} from '../../store/api/localApi';
import { PolicyStatusPill } from '../../components/policies/PolicyStatusPill';
import PolicyDocumentUpload from '../../components/policies/PolicyDocumentUpload';
import PolicyAudienceControl from '../../components/policies/PolicyAudienceControl';
import PolicyAudienceDialog from '../../components/policies/PolicyAudienceDialog';
import {
  EVERYONE_AT_LOCATION,
  audienceLabel,
  useLocationClassNames,
} from '../../components/policies/audienceLabel';
import PolicyResponses from './PolicyResponses';
import { BackLink } from '../../components/BackLink';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/Toast';
import { ParentAction, PolicyAudience, PolicyStatus } from '../../types';

/**
 * Lazy: react-pdf + the pdf.js API layer add ~480 kB to whatever chunk imports
 * them (measured). Almost nobody who opens a policy's detail page goes on to
 * read the PDF in-app — most check the version or the coverage — so making
 * every page load in the whole app carry that weight is a bad trade. Split
 * here, loaded on the first "View the document" tap.
 */
const PolicyPdfViewer = React.lazy(() => import('../../components/policies/PolicyPdfViewer'));

const PARENT_ACTION_LABELS: Record<ParentAction, string> = {
  [ParentAction.NONE]: 'Not shown to parents',
  [ParentAction.VIEW]: 'Parents can read it',
  [ParentAction.ACKNOWLEDGE]: 'Parents tap to confirm',
  [ParentAction.SIGN]: 'Parents sign with their name',
};

/**
 * One policy: its versions, its review history, and who has responded.
 *
 * Who has responded lives in <PolicyResponses>; this page only decides that an
 * admin with a published version gets to see it, and hands it the coverage it
 * already loaded.
 */
const PolicyDetail: React.FC = () => {
  const { policyId = '' } = useParams<{ policyId: string }>();
  // Everything on this page except reading the document is an ADMIN act, and
  // the backend agrees: policy:manage guards every write and policy:view-coverage
  // guards the coverage read, neither of which STAFF holds. Verified live —
  // PATCH, versions, nudge and reviews all 403 for a staff member, and so does
  // coverage.
  //
  // So none of this was a security hole; it was worse as UX than as risk. A
  // staff member opening a policy saw the parent-ask dropdown, "Publish the
  // change", paper-based settings, the staff-ack toggle, review and version
  // history, and — the part that actually matters — the coverage panel naming
  // which colleagues have NOT complied. Coverage is a supervisory view; the
  // module's own STAFF grant comment says so.
  //
  // Staff read the policy. That is the whole job.
  const { isAdmin } = useAuth();

  const { data, isLoading } = useGetPolicyQuery(policyId, { skip: !policyId });
  // Skipped for staff: the request 403s, so firing it only produced a console
  // error on every policy a staff member opened.
  const { data: coverage } = useGetPolicyCoverageQuery(policyId, {
    skip: !policyId || !isAdmin,
  });
  const [recordReview, { isLoading: isReviewing }] = useRecordPolicyReviewMutation();
  const [setPaperBased] = useSetPolicyPaperBasedMutation();
  const [createVersion, { isLoading: isPublishing }] = useCreatePolicyVersionMutation();
  const [updatePolicy, { isLoading: isSavingStaffAck }] = useUpdatePolicyMutation();
  const [fetchFile, fileResult] = useLazyGetPolicyVersionFileQuery();
  const { showError } = useToast();

  const [paperNote, setPaperNote] = useState('');
  const [parentAction, setParentAction] = useState<ParentAction | null>(null);

  // Document-upload state. `pendingFileKey` holds the s3Key of a PDF that has
  // been PUT to S3 but not yet published as a version; `isUploading` gates the
  // Publish button while a PUT is in flight.
  const [pendingFileKey, setPendingFileKey] = useState<string | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);
  // First-upload parent-ask (NOT_ADDED). Seeded from the same default as the
  // change-ask dropdown once the policy loads (see the effect below).
  const [uploadParentAction, setUploadParentAction] = useState<ParentAction | null>(null);
  // New-version material flag (ACTIVE): a material change re-notifies guardians
  // and resets their acknowledgements; non-material carries acks forward.
  const [newVersionMaterial, setNewVersionMaterial] = useState(false);
  // Who a publish is sent to. `null` = "Chosen classes" with none ticked, which
  // disables both publish buttons. Seeded from the stored audience below.
  const [publishAudience, setPublishAudience] = useState<PolicyAudience | null>(EVERYONE_AT_LOCATION);
  const [showAudienceDialog, setShowAudienceDialog] = useState(false);
  // Class names for the "Sent to" summary and the review history. Admin-only:
  // staff see neither.
  const classNames = useLocationClassNames(data?.policy.locationId ?? '', !isAdmin);

  /**
   * Seed the ask dropdown once the policy has loaded, rather than in the
   * useState initialiser — hooks run before the loading guard, so `policy` is
   * not available up there.
   *
   * A CUSTOM policy (no checklist slot) starts at NONE. A staff handbook is a
   * custom policy, and defaulting it to "parents can read it" would pre-select
   * publishing an internal document to every guardian. The nine statutory slots
   * are parent-facing by nature, so they keep VIEW.
   *
   * The current version's own setting wins when there is one; this default only
   * applies before anything has been published.
   */
  const currentParentAction = data?.versions.find(
    (v) => v.versionId === data.policy.currentVersionId
  )?.parentAction;
  const checklistKey = data?.policy.checklistKey;
  const hasLoaded = !!data;
  // A string key so the effect re-seeds only when the stored audience really
  // changes (e.g. after the Change dialog saves), not on every refetch.
  const storedAudienceKey = JSON.stringify(data?.policy.audience ?? EVERYONE_AT_LOCATION);

  // A publish defaults to whoever the policy is already sent to, so uploading
  // a new version never quietly widens a policy back to everyone.
  useEffect(() => {
    if (!hasLoaded) return;
    setPublishAudience(JSON.parse(storedAudienceKey) as PolicyAudience);
  }, [hasLoaded, storedAudienceKey]);

  useEffect(() => {
    if (!hasLoaded) return;
    const seededAsk = currentParentAction ?? (checklistKey ? ParentAction.VIEW : ParentAction.NONE);
    setParentAction(seededAsk);
    setUploadParentAction(seededAsk);
  }, [hasLoaded, currentParentAction, checklistKey]);

  if (isLoading || !data) {
    return (
      <div data-testid="policy-detail-loading" className="py-10 text-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  const { policy, versions, reviews } = data;
  const storedAudience: PolicyAudience = policy.audience ?? EVERYONE_AT_LOCATION;
  const currentVersion = versions.find((v) => v.versionId === policy.currentVersionId);

  const handleViewFile = async () => {
    if (!currentVersion) return;
    await fetchFile({ policyId, versionId: currentVersion.versionId });
  };

  // A settings-only republish: no new file, so the previous document carries
  // forward. This exists so changing the ask stays a deliberate publish rather
  // than a dropdown toggle that could mass-notify on a mis-click.
  //
  // It sends no `audience` on purpose: the server keeps the stored one, so
  // changing the ask can never change who is asked.
  const handleChangeAsk = async () => {
    if (!parentAction) return;
    await createVersion({
      policyId,
      parentAction,
      effectiveDate: new Date().toISOString().split('T')[0],
      changeNote: `Changed what parents are asked to ${PARENT_ACTION_LABELS[parentAction]}`,
      isSettingsOnly: true,
    }).unwrap();
  };

  // Policy-level, NOT version-level: who on the staff must read this is a
  // setting, not a change to what anyone is asked, so it must not publish a new
  // version the way the parent-facing ask deliberately does.
  const handleToggleStaffAck = async () => {
    await updatePolicy({ policyId, staffAckRequired: !policy.staffAckRequired }).unwrap();
  };

  // First document into an empty policy: upload the PDF, choose what parents
  // are asked, and publish version 1 in one deliberate step. version 1 always
  // notifies (it is the first ask), so no material flag here.
  const handlePublishFirst = async () => {
    if (!pendingFileKey || !uploadParentAction || !publishAudience) return;
    try {
      await createVersion({
        policyId,
        fileKey: pendingFileKey,
        parentAction: uploadParentAction,
        effectiveDate: new Date().toISOString().split('T')[0],
        audience: publishAudience,
      }).unwrap();
      setPendingFileKey(undefined);
    } catch (err) {
      showError(
        (err as { data?: { error?: string } })?.data?.error || 'Failed to publish the document.',
        'policy-upload-error'
      );
    }
  };

  // A new version of an existing document: upload a new PDF, keep the current
  // parent-ask, and choose whether it is a material change (re-notify guardians
  // and reset their acknowledgements) or not (acks carry forward).
  const handleUploadNewVersion = async () => {
    if (!pendingFileKey || !currentVersion || !publishAudience) return;
    try {
      await createVersion({
        policyId,
        fileKey: pendingFileKey,
        parentAction: currentVersion.parentAction,
        effectiveDate: new Date().toISOString().split('T')[0],
        changeNote: 'New document version',
        materialChange: newVersionMaterial,
        audience: publishAudience,
      }).unwrap();
      setPendingFileKey(undefined);
      setNewVersionMaterial(false);
    } catch (err) {
      showError(
        (err as { data?: { error?: string } })?.data?.error || 'Failed to publish the new version.',
        'policy-upload-error'
      );
    }
  };

  return (
    <div data-testid="policy-detail" className="space-y-6">
      <BackLink fallbackTo="/policies" data-testid="policy-detail-back" />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{policy.title}</h1>
          {policy.summary && <p className="mt-1 text-sm text-gray-600">{policy.summary}</p>}
        </div>
        <PolicyStatusPill status={policy.status} />
      </div>

      {/*
        A policy-level setting, so it saves immediately and does NOT publish a
        version — unlike the parent-facing ask below, where a deliberate
        republish is the whole point. Staff are only ever asked to ACKNOWLEDGE;
        signing is a parent-only act.
      */}
      {isAdmin && (
        <div
          data-testid="policy-staff-ack-panel"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              data-testid="policy-staff-ack-toggle"
              checked={policy.staffAckRequired}
              disabled={isSavingStaffAck}
              onChange={handleToggleStaffAck}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 disabled:opacity-50"
            />
            <span className="text-sm text-gray-700">Staff must acknowledge this policy</span>
          </label>
          <p data-testid="policy-staff-ack-state" className="mt-1 text-xs text-gray-500">
            {policy.staffAckRequired
              ? 'Every staff member at this location is asked to confirm they have read it.'
              : 'Staff are not asked to confirm anything for this policy.'}
          </p>
        </div>
      )}

      {/*
        Who the policy is for. Admin-only like the rest of the supervisory
        settings: staff behaviour is location-wide regardless. Shown once a
        document is published — before that the first-publish panel holds the
        same choice. Changing it saves straight away (no new version); the
        server records it in the review history.
      */}
      {isAdmin && currentVersion && (
        <div
          data-testid="policy-audience-panel"
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <p data-testid="policy-audience" className="text-sm text-gray-700">
            Sent to: {audienceLabel(storedAudience, classNames)}
          </p>
          <button
            type="button"
            data-testid="policy-audience-change"
            onClick={() => setShowAudienceDialog(true)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Change
          </button>
        </div>
      )}
      {showAudienceDialog && (
        <PolicyAudienceDialog
          policyId={policyId}
          locationId={policy.locationId}
          current={storedAudience}
          onClose={() => setShowAudienceDialog(false)}
        />
      )}

      {isAdmin && policy.status === PolicyStatus.NOT_ADDED && (
        <div
          data-testid="policy-upload-panel"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <p className="text-sm font-medium text-gray-900">Add this policy's document</p>
          <p className="mt-1 text-xs text-gray-500">
            Upload the PDF and choose what parents are asked. Publishing puts it in the vault.
          </p>

          <div className="mt-3">
            <PolicyDocumentUpload
              locationId={policy.locationId}
              onChange={setPendingFileKey}
              onUploadingChange={setIsUploading}
              inputTestId="policy-upload-input"
              ariaLabel="Choose the policy document (PDF)"
            />
          </div>

          <label
            htmlFor="policy-upload-ask"
            className="mt-4 block text-sm font-medium text-gray-700"
          >
            What are parents asked?
          </label>
          <select
            id="policy-upload-ask"
            data-testid="policy-upload-ask"
            value={uploadParentAction ?? ''}
            onChange={(e) => setUploadParentAction(e.target.value as ParentAction)}
            className="mt-1 rounded-md border-gray-300 text-sm shadow-sm"
          >
            {Object.values(ParentAction).map((a) => (
              <option key={a} value={a}>
                {PARENT_ACTION_LABELS[a]}
              </option>
            ))}
          </select>

          <div className="mt-4">
            <PolicyAudienceControl
              locationId={policy.locationId}
              value={publishAudience}
              onChange={setPublishAudience}
            />
          </div>

          <div className="mt-4">
            <button
              type="button"
              data-testid="policy-upload-publish"
              disabled={!pendingFileKey || !publishAudience || isUploading || isPublishing}
              onClick={handlePublishFirst}
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {isUploading ? 'Uploading…' : 'Publish document'}
            </button>
            {/* The disabled state alone (a slightly lighter blue) reads as
                broken rather than "waiting for a file" — say so explicitly.
                Only for the no-file-yet case; isUploading already has its
                own "Uploading…" label on the button itself. */}
            {!pendingFileKey && !isUploading && (
              <p className="mt-1.5 text-xs text-gray-500" data-testid="policy-upload-publish-hint">
                Choose a PDF above first.
              </p>
            )}
          </div>
        </div>
      )}

      {isAdmin && policy.status === PolicyStatus.NOT_ADDED && (
        <div
          data-testid="policy-paper-panel"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <p className="text-sm text-gray-700">
            Or, if you hold this on paper, you can record where it lives instead — that is a
            perfectly good answer.
          </p>
          <input
            data-testid="policy-paper-note"
            value={paperNote}
            onChange={(e) => setPaperNote(e.target.value)}
            placeholder="e.g. Office filing cabinet, blue folder"
            className="mt-3 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
          <button
            type="button"
            data-testid="policy-paper-save"
            onClick={() => setPaperBased({ policyId, paperBased: true, note: paperNote })}
            className="mt-3 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            We hold this on paper
          </button>
        </div>
      )}

      {policy.status === PolicyStatus.PAPER_BASED && (
        <div
          data-testid="policy-paper-summary"
          className="rounded-lg border border-blue-200 bg-blue-50 p-4"
        >
          {/* Staff SHOULD see this line — it tells them where the document
              actually lives, which is the one thing they need from a paper
              policy. Only the Undo, which is a change, is admin-only. */}
          <p className="text-sm text-blue-900">
            Held on paper{policy.paperNote ? ` — ${policy.paperNote}` : ''}.
          </p>
          {isAdmin && (
            <button
              type="button"
              data-testid="policy-paper-unset"
              onClick={() => setPaperBased({ policyId, paperBased: false })}
              className="mt-2 text-sm font-medium text-blue-800 underline"
            >
              Undo
            </button>
          )}
        </div>
      )}

      {currentVersion && (
        <div
          data-testid="policy-current-version"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          {/* The version NUMBER and the parent-ask label are both admin
              framing: what parents are asked ("Parents sign with their name",
              "Not shown to parents") is a supervisory setting, and a staff
              member reading the fire safety procedure does not need to know it
              is v3. They get the effective date, which is the one fact that
              tells them whether what they are reading is current. */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-gray-900">
              {isAdmin
                ? `Version ${currentVersion.versionNumber} · effective ${currentVersion.effectiveDate}`
                : `In effect since ${currentVersion.effectiveDate}`}
            </p>
            {isAdmin && (
              <span data-testid="policy-parent-action" className="text-xs text-gray-600">
                {PARENT_ACTION_LABELS[currentVersion.parentAction]}
              </span>
            )}
          </div>

          <button
            type="button"
            data-testid="policy-view-document"
            onClick={handleViewFile}
            className="mt-3 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            View the document
          </button>

          {fileResult.data?.url && (
            <div className="mt-4">
              <React.Suspense
                fallback={
                  <div
                    data-testid="policy-pdf-loading"
                    className="py-12 text-center text-sm text-gray-500"
                  >
                    Loading the document viewer…
                  </div>
                }
              >
                <PolicyPdfViewer url={fileResult.data.url} onRefresh={handleViewFile} />
              </React.Suspense>
            </div>
          )}

          {isAdmin && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <label
                htmlFor="policy-ask-select"
                className="block text-sm font-medium text-gray-700"
              >
                Change what parents are asked
              </label>
              <p className="mt-1 text-xs text-gray-500">
                This publishes a new version of the same document, so the change is recorded and
                deliberate.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <select
                  id="policy-ask-select"
                  data-testid="policy-ask-select"
                  value={parentAction ?? ''}
                  onChange={(e) => setParentAction(e.target.value as ParentAction)}
                  className="rounded-md border-gray-300 text-sm shadow-sm"
                >
                  {Object.values(ParentAction).map((a) => (
                    <option key={a} value={a}>
                      {PARENT_ACTION_LABELS[a]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid="policy-ask-save"
                  disabled={isPublishing}
                  onClick={handleChangeAsk}
                  className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  Publish the change
                </button>
              </div>
            </div>
          )}

          {isAdmin && (
            <div
              data-testid="policy-new-version-panel"
              className="mt-4 border-t border-gray-100 pt-4"
            >
              <p className="text-sm font-medium text-gray-700">Upload a new version</p>
              <p className="mt-1 text-xs text-gray-500">
                Replaces the document with a new PDF, recorded as a new version.
              </p>

              <div className="mt-2">
                <PolicyDocumentUpload
                  locationId={policy.locationId}
                  onChange={setPendingFileKey}
                  onUploadingChange={setIsUploading}
                  inputTestId="policy-new-version-input"
                  ariaLabel="Choose a new version of the policy document (PDF)"
                />
              </div>

              <label className="mt-3 flex items-start gap-2">
                <input
                  type="checkbox"
                  data-testid="policy-new-version-material"
                  checked={newVersionMaterial}
                  onChange={(e) => setNewVersionMaterial(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                <span className="text-sm text-gray-700">
                  Material change
                  <span className="block text-xs text-gray-500">
                    Re-notify guardians and ask them to acknowledge this version again. Leave off
                    for a typo or minor fix — existing acknowledgements carry forward.
                  </span>
                </span>
              </label>

              <div className="mt-3">
                <PolicyAudienceControl
                  locationId={policy.locationId}
                  value={publishAudience}
                  onChange={setPublishAudience}
                />
              </div>

              <div className="mt-3">
                <button
                  type="button"
                  data-testid="policy-new-version-publish"
                  disabled={!pendingFileKey || !publishAudience || isUploading || isPublishing}
                  onClick={handleUploadNewVersion}
                  className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {isUploading ? 'Uploading…' : 'Publish new version'}
                </button>
                {!pendingFileKey && !isUploading && (
                  <p
                    className="mt-1.5 text-xs text-gray-500"
                    data-testid="policy-new-version-publish-hint"
                  >
                    Choose a PDF above first.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {isAdmin && coverage && coverage.version && (
        <PolicyResponses policyId={policyId} coverage={coverage} />
      )}

      {/* Review and version history are the vault's INSPECTION record — who
          reviewed the policy and when, what changed between versions. That is
          what an admin shows Tusla, not something a staff member needs to do
          their job. Recording a review is policy:manage and 403s for staff. */}
      {isAdmin && (
        <div
          data-testid="policy-reviews"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-900">Review history</h2>
            <button
              type="button"
              data-testid="policy-record-review"
              disabled={isReviewing}
              onClick={() => recordReview({ policyId, note: 'Reviewed — no change needed' })}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Reviewed — no change
            </button>
          </div>

          {reviews.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">No reviews recorded yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {reviews.map((r) => (
                <li
                  key={r.reviewId}
                  data-testid="policy-review-row"
                  className="text-sm text-gray-700"
                >
                  {r.reviewedAt.split('T')[0]} ·{' '}
                  {r.outcome === 'AUDIENCE_CHANGED' && r.audienceChange ? (
                    <span data-testid={`policy-review-audience-${r.reviewId}`}>
                      Sent to changed: {audienceLabel(r.audienceChange.from, classNames)} →{' '}
                      {audienceLabel(r.audienceChange.to, classNames)}
                    </span>
                  ) : r.outcome === 'NO_CHANGE' ? (
                    'No change'
                  ) : (
                    'New version'
                  )}{' '}
                  · {r.reviewedByName}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isAdmin && versions.length > 0 && (
        <div
          data-testid="policy-versions"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-gray-900">Version history</h2>
          <ul className="mt-3 space-y-2">
            {versions.map((v) => (
              <li
                key={v.versionId}
                data-testid="policy-version-row"
                className="text-sm text-gray-700"
              >
                v{v.versionNumber} · {v.effectiveDate}
                {v.changeNote ? ` · ${v.changeNote}` : ''}
                {v.isSettingsOnly ? ' · settings only' : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PolicyDetail;
